import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { usePermissions } from "@/hooks/usePermissions";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Eye, Edit, Trash2, Plus, ArrowUpDown, Check, ChevronsUpDown, Filter, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

interface Local {
  id: string;
  nome: string;
  frete_moto?: number;
  frete_carro_hatch?: number;
  frete_utilitario?: number;
}

interface Frete {
  id: string;
  local_id: string;
  tipo_frete: string;
  data: string;
  valor: number;
  observacao?: string;
  orcamento?: string;
  locais?: { nome: string };
}

export default function Fretes() {
  const { hasPermission } = usePermissions();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [fretes, setFretes] = useState<Frete[]>([]);
  const [locais, setLocais] = useState<Local[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedFrete, setSelectedFrete] = useState<Frete | null>(null);
  const [openLocalCombo, setOpenLocalCombo] = useState(false);
  const [openLocalEditCombo, setOpenLocalEditCombo] = useState(false);
  const [sortField, setSortField] = useState<keyof Frete | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [filterText, setFilterText] = useState("");
  const [filters, setFilters] = useState({
    local: "",
    tipo_frete: "",
    data_inicio: "",
    data_fim: "",
    valor_min: "",
    valor_max: "",
    observacao: "",
    orcamento: ""
  });
  
  const [formData, setFormData] = useState({
    id: "",
    local_id: "",
    tipo_frete: "",
    data: new Date().toLocaleDateString('en-CA'), // Format YYYY-MM-DD
    valor: 0,
    observacao: "",
    orcamento: ""
  });

  const tipoFretes = ["Moto", "Carro Hatch", "Utilitário"];

  // Verificar permissões
  const canView = hasPermission('produtos_insumos', 'fretes', 'read');
  const canCreate = hasPermission('produtos_insumos', 'fretes', 'write');
  const canUpdate = hasPermission('produtos_insumos', 'fretes', 'write');
  const canDelete = hasPermission('produtos_insumos', 'fretes', 'admin');

  useEffect(() => {
    if (canView) {
      fetchFretes();
      fetchLocais();
    }
  }, [canView]);

  const fetchFretes = async () => {
    try {
      const { data, error } = await supabase
        .from('fretes')
        .select(`
          *,
          locais(nome)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      // @ts-ignore - Temporary fix for type issues
      setFretes((data || []).map(frete => ({ 
        ...frete, 
        locais: (frete.locais && typeof frete.locais === 'object')
          ? frete.locais 
          : { nome: '' }
      })));
    } catch (error: any) {
      toast({
        title: "Erro ao carregar fretes",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchLocais = async () => {
    try {
      // @ts-ignore - Temporary fix for deep recursion type error
      const { data, error } = await supabase
        .from('locais')
        .select('id, nome, frete_moto, frete_carro_hatch, frete_utilitario')
        .eq('ativo', true)
        .order('nome');

      if (error) throw error;
      setLocais(data || []);
    } catch (error: any) {
      toast({
        title: "Erro ao carregar locais",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (formData.id) {
        // Editar
        const { error } = await supabase
          .from('fretes')
          .update({
            local_id: formData.local_id,
            tipo_frete: formData.tipo_frete,
            data: formData.data,
            valor: formData.valor,
            observacao: formData.observacao,
            orcamento: formData.orcamento,
            updated_by: user?.id
          })
          .eq('id', formData.id);

        if (error) throw error;

        toast({
          title: "Frete atualizado com sucesso!",
        });
      } else {
        // Criar
        const { error } = await supabase
          .from('fretes')
          .insert([{
            local_id: formData.local_id,
            tipo_frete: formData.tipo_frete,
            data: formData.data,
            valor: formData.valor,
            observacao: formData.observacao,
            orcamento: formData.orcamento,
            created_by: user?.id
          }]);

        if (error) throw error;

        toast({
          title: "Frete criado com sucesso!",
        });
      }

      setIsDialogOpen(false);
      setIsEditDialogOpen(false);
      resetForm();
      fetchFretes();
    } catch (error: any) {
      toast({
        title: formData.id ? "Erro ao atualizar frete" : "Erro ao criar frete",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const resetForm = () => {
    setFormData({
      id: "",
      local_id: "",
      tipo_frete: "",
      data: new Date().toLocaleDateString('en-CA'), // Format YYYY-MM-DD
      valor: 0,
      observacao: "",
      orcamento: ""
    });
  };

  const handleLocalChange = (localId: string) => {
    const local = locais.find(l => l.id === localId);
    if (!local) return;

    setFormData(prev => ({
      ...prev,
      local_id: localId,
      valor: getFreteValue(local, prev.tipo_frete)
    }));
  };

  const handleTipoFreteChange = (tipoFrete: string) => {
    const local = locais.find(l => l.id === formData.local_id);
    
    setFormData(prev => ({
      ...prev,
      tipo_frete: tipoFrete,
      valor: local ? getFreteValue(local, tipoFrete) : 0
    }));
  };

  const handleView = (frete: Frete) => {
    setSelectedFrete(frete);
    setIsViewDialogOpen(true);
  };

  const handleEdit = (frete: Frete) => {
    setFormData({
      id: frete.id,
      local_id: frete.local_id,
      tipo_frete: frete.tipo_frete,
      data: frete.data,
      valor: frete.valor,
      observacao: frete.observacao || "",
      orcamento: frete.orcamento || ""
    });
    setIsEditDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('fretes')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Frete excluído com sucesso!",
      });

      fetchFretes();
    } catch (error: any) {
      toast({
        title: "Erro ao excluir frete",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const getFreteValue = (local: Local, tipoFrete: string): number => {
    switch (tipoFrete) {
      case "Moto":
        return local.frete_moto || 0;
      case "Carro Hatch":
        return local.frete_carro_hatch || 0;
      case "Utilitário":
        return local.frete_utilitario || 0;
      default:
        return 0;
    }
  };

  const handleSort = (field: keyof Frete) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredAndSortedFretes = fretes
    .filter(frete => {
      // Filtro de texto global
      const matchesText = filterText === "" || [
        frete.locais?.nome,
        frete.tipo_frete,
        frete.data,
        frete.valor.toString(),
        frete.observacao,
        frete.orcamento
      ].some(field => field?.toString().toLowerCase().includes(filterText.toLowerCase()));

      // Filtros específicos
      const matchesLocal = filters.local === "" || 
        frete.locais?.nome?.toLowerCase().includes(filters.local.toLowerCase());
      
      const matchesTipoFrete = filters.tipo_frete === "" || 
        frete.tipo_frete.toLowerCase().includes(filters.tipo_frete.toLowerCase());
      
      const matchesDataInicio = filters.data_inicio === "" || 
        new Date(frete.data) >= new Date(filters.data_inicio);
      
      const matchesDataFim = filters.data_fim === "" || 
        new Date(frete.data) <= new Date(filters.data_fim);
      
      const matchesValorMin = filters.valor_min === "" || 
        frete.valor >= parseFloat(filters.valor_min);
      
      const matchesValorMax = filters.valor_max === "" || 
        frete.valor <= parseFloat(filters.valor_max);
      
      const matchesObservacao = filters.observacao === "" || 
        frete.observacao?.toLowerCase().includes(filters.observacao.toLowerCase());
      
      const matchesOrcamento = filters.orcamento === "" || 
        frete.orcamento?.toLowerCase().includes(filters.orcamento.toLowerCase());

      return matchesText && matchesLocal && matchesTipoFrete && 
             matchesDataInicio && matchesDataFim && matchesValorMin && 
             matchesValorMax && matchesObservacao && matchesOrcamento;
    })
    .sort((a, b) => {
      if (!sortField) return 0;
      
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      if (sortField === 'locais') {
        aValue = a.locais?.nome || '';
        bValue = b.locais?.nome || '';
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  // Calcular totais dos fretes filtrados
  const totalRegistros = filteredAndSortedFretes.length;
  const valorTotal = filteredAndSortedFretes.reduce((total, frete) => total + frete.valor, 0);

  const clearFilters = () => {
    setFilters({
      local: "",
      tipo_frete: "",
      data_inicio: "",
      data_fim: "",
      valor_min: "",
      valor_max: "",
      observacao: "",
      orcamento: ""
    });
    setFilterText("");
  };

  if (!canView) {
    return (
      <div className="p-8">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">
              Você não tem permissão para acessar esta página.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-8">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center">Carregando...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Fretes</h1>
        {canCreate && (
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Novo Frete
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Novo Frete</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="local_id">Local</Label>
                  <Popover open={openLocalCombo} onOpenChange={setOpenLocalCombo}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={openLocalCombo}
                        className="w-full justify-between"
                      >
                        {formData.local_id
                          ? locais.find((local) => local.id === formData.local_id)?.nome
                          : "Selecione um local..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0 z-50 bg-background border">
                      <Command>
                        <CommandInput placeholder="Digite para buscar local..." />
                        <CommandList>
                          <CommandEmpty>Nenhum local encontrado.</CommandEmpty>
                          <CommandGroup>
                            {locais.map((local) => (
                              <CommandItem
                                key={local.id}
                                value={local.nome}
                                onSelect={() => {
                                  handleLocalChange(local.id);
                                  setOpenLocalCombo(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    formData.local_id === local.id ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                {local.nome}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>

                <div>
                  <Label htmlFor="tipo_frete">Tipo de Frete</Label>
                  <Select
                    value={formData.tipo_frete}
                    onValueChange={handleTipoFreteChange}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      {tipoFretes.map((tipo) => (
                        <SelectItem key={tipo} value={tipo}>
                          {tipo}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="data">Data</Label>
                  <Input
                    id="data"
                    type="date"
                    value={formData.data}
                    onChange={(e) => setFormData(prev => ({ ...prev, data: e.target.value }))}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="valor">Valor</Label>
                  <Input
                    id="valor"
                    type="number"
                    step="0.01"
                    value={formData.valor}
                    readOnly
                    className="bg-muted"
                  />
                </div>

                <div>
                  <Label htmlFor="observacao">Observação</Label>
                  <Input
                    id="observacao"
                    type="text"
                    value={formData.observacao}
                    onChange={(e) => setFormData(prev => ({ ...prev, observacao: e.target.value }))}
                    placeholder="Digite uma observação (opcional)"
                  />
                </div>

                <div>
                  <Label htmlFor="orcamento">Orçamento</Label>
                  <Input
                    id="orcamento"
                    type="text"
                    value={formData.orcamento}
                    onChange={(e) => setFormData(prev => ({ ...prev, orcamento: e.target.value }))}
                    placeholder="Digite o orçamento (opcional)"
                  />
                </div>

                <div className="flex justify-end space-x-2">
                  <Button type="button" variant="outline" onClick={() => { setIsDialogOpen(false); resetForm(); }}>
                    Cancelar
                  </Button>
                  <Button type="submit">Salvar</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Dialog de Visualização */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Visualizar Frete</DialogTitle>
          </DialogHeader>
          {selectedFrete && (
            <div className="space-y-4">
              <div>
                <Label>Local</Label>
                <p className="text-sm text-muted-foreground mt-1">{selectedFrete.locais?.nome}</p>
              </div>
              <div>
                <Label>Tipo de Frete</Label>
                <p className="text-sm text-muted-foreground mt-1">{selectedFrete.tipo_frete}</p>
              </div>
              <div>
                <Label>Data</Label>
                <p className="text-sm text-muted-foreground mt-1">{new Date(selectedFrete.data + 'T00:00:00').toLocaleDateString('pt-BR')}</p>
              </div>
              <div>
                <Label>Valor</Label>
                <p className="text-sm text-muted-foreground mt-1">R$ {selectedFrete.valor.toFixed(2)}</p>
              </div>
              <div>
                <Label>Observação</Label>
                <p className="text-sm text-muted-foreground mt-1">{selectedFrete.observacao || "Nenhuma observação"}</p>
              </div>
              <div>
                <Label>Orçamento</Label>
                <p className="text-sm text-muted-foreground mt-1">{selectedFrete.orcamento || "Nenhum orçamento informado"}</p>
              </div>
              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setIsViewDialogOpen(false)}>
                  Fechar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog de Edição */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Editar Frete</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="edit_local_id">Local</Label>
              <Popover open={openLocalEditCombo} onOpenChange={setOpenLocalEditCombo}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={openLocalEditCombo}
                    className="w-full justify-between"
                  >
                    {formData.local_id
                      ? locais.find((local) => local.id === formData.local_id)?.nome
                      : "Selecione um local..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0 z-50 bg-background border">
                  <Command>
                    <CommandInput placeholder="Digite para buscar local..." />
                    <CommandList>
                      <CommandEmpty>Nenhum local encontrado.</CommandEmpty>
                      <CommandGroup>
                        {locais.map((local) => (
                          <CommandItem
                            key={local.id}
                            value={local.nome}
                            onSelect={() => {
                              handleLocalChange(local.id);
                              setOpenLocalEditCombo(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                formData.local_id === local.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            {local.nome}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <Label htmlFor="edit_tipo_frete">Tipo de Frete</Label>
              <Select
                value={formData.tipo_frete}
                onValueChange={handleTipoFreteChange}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  {tipoFretes.map((tipo) => (
                    <SelectItem key={tipo} value={tipo}>
                      {tipo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="edit_data">Data</Label>
              <Input
                id="edit_data"
                type="date"
                value={formData.data}
                onChange={(e) => setFormData(prev => ({ ...prev, data: e.target.value }))}
                required
              />
            </div>

            <div>
              <Label htmlFor="edit_valor">Valor</Label>
              <Input
                id="edit_valor"
                type="number"
                step="0.01"
                value={formData.valor}
                readOnly
                className="bg-muted"
              />
            </div>

            <div>
              <Label htmlFor="edit_observacao">Observação</Label>
              <Input
                id="edit_observacao"
                type="text"
                value={formData.observacao}
                onChange={(e) => setFormData(prev => ({ ...prev, observacao: e.target.value }))}
                placeholder="Digite uma observação (opcional)"
              />
            </div>

            <div>
              <Label htmlFor="edit_orcamento">Orçamento</Label>
              <Input
                id="edit_orcamento"
                type="text"
                value={formData.orcamento}
                onChange={(e) => setFormData(prev => ({ ...prev, orcamento: e.target.value }))}
                placeholder="Digite o orçamento (opcional)"
              />
            </div>

            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => { setIsEditDialogOpen(false); resetForm(); }}>
                Cancelar
              </Button>
              <Button type="submit">Atualizar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Resumo */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">{totalRegistros}</div>
              <div className="text-sm text-muted-foreground">Total de Registros</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">R$ {valorTotal.toFixed(2)}</div>
              <div className="text-sm text-muted-foreground">Valor Total dos Fretes</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Lista de Fretes</CardTitle>
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Filter className="h-4 w-4 mr-2" />
                  Filtros Avançados
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                  <DialogTitle>Filtros Avançados</DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="filter_local">Local</Label>
                    <Input
                      id="filter_local"
                      placeholder="Filtrar por local..."
                      value={filters.local}
                      onChange={(e) => setFilters(prev => ({ ...prev, local: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="filter_tipo_frete">Tipo de Frete</Label>
                    <Select 
                      value={filters.tipo_frete || "todos"} 
                      onValueChange={(value) => setFilters(prev => ({ ...prev, tipo_frete: value === "todos" ? "" : value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Todos os tipos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todos">Todos os tipos</SelectItem>
                        {tipoFretes.map((tipo) => (
                          <SelectItem key={tipo} value={tipo}>
                            {tipo}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="filter_data_inicio">Data Início</Label>
                    <Input
                      id="filter_data_inicio"
                      type="date"
                      value={filters.data_inicio}
                      onChange={(e) => setFilters(prev => ({ ...prev, data_inicio: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="filter_data_fim">Data Fim</Label>
                    <Input
                      id="filter_data_fim"
                      type="date"
                      value={filters.data_fim}
                      onChange={(e) => setFilters(prev => ({ ...prev, data_fim: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="filter_valor_min">Valor Mínimo</Label>
                    <Input
                      id="filter_valor_min"
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={filters.valor_min}
                      onChange={(e) => setFilters(prev => ({ ...prev, valor_min: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="filter_valor_max">Valor Máximo</Label>
                    <Input
                      id="filter_valor_max"
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={filters.valor_max}
                      onChange={(e) => setFilters(prev => ({ ...prev, valor_max: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="filter_observacao">Observação</Label>
                    <Input
                      id="filter_observacao"
                      placeholder="Filtrar por observação..."
                      value={filters.observacao}
                      onChange={(e) => setFilters(prev => ({ ...prev, observacao: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="filter_orcamento">Orçamento</Label>
                    <Input
                      id="filter_orcamento"
                      placeholder="Filtrar por orçamento..."
                      value={filters.orcamento}
                      onChange={(e) => setFilters(prev => ({ ...prev, orcamento: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-2 mt-4">
                  <Button variant="outline" onClick={clearFilters}>
                    <X className="h-4 w-4 mr-2" />
                    Limpar Filtros
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <Input
              placeholder="Busca rápida em todos os campos..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="max-w-sm"
            />
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead 
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('locais' as keyof Frete)}
                >
                  <div className="flex items-center space-x-1">
                    <span>Local</span>
                    <ArrowUpDown className="h-4 w-4" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('tipo_frete')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Tipo de Frete</span>
                    <ArrowUpDown className="h-4 w-4" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('data')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Data</span>
                    <ArrowUpDown className="h-4 w-4" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('valor')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Valor</span>
                    <ArrowUpDown className="h-4 w-4" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('observacao')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Observação</span>
                    <ArrowUpDown className="h-4 w-4" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('orcamento')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Orçamento</span>
                    <ArrowUpDown className="h-4 w-4" />
                  </div>
                </TableHead>
                <TableHead>Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAndSortedFretes.map((frete) => (
                <TableRow key={frete.id}>
                  <TableCell>{frete.locais?.nome}</TableCell>
                  <TableCell>{frete.tipo_frete}</TableCell>
                  <TableCell>{new Date(frete.data + 'T00:00:00').toLocaleDateString('pt-BR')}</TableCell>
                  <TableCell>R$ {frete.valor.toFixed(2)}</TableCell>
                  <TableCell>{frete.observacao || "-"}</TableCell>
                  <TableCell>{frete.orcamento || "-"}</TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button variant="outline" size="icon" onClick={() => handleView(frete)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      {canUpdate && (
                        <Button variant="outline" size="icon" onClick={() => handleEdit(frete)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                      )}
                      {canDelete && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="icon">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
                              <AlertDialogDescription>
                                Tem certeza que deseja excluir este frete? Esta ação não pode ser desfeita.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDelete(frete.id)}>
                                Excluir
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}